import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { cartAPI } from '../config/api';
import resolveImageUrl from '../utils/resolveImageUrl';
import {
  formatMoney,
  formatRentalDate,
  getBookedFor,
  getBookedFrom,
  getDropoffTime,
  getRentalDays,
  getRentalLineTotal,
} from '../utils/rentalFields';
import { showAppToast } from '../utils/notifications';
import { trackBeginCheckout } from '../utils/analytics';
import swal from 'sweetalert';

function Cart({ cartItems, totalCost, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [localCartItems, setLocalCartItems] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    setLocalCartItems(cartItems || []);
  }, [cartItems]);

  const subtotal = useMemo(() => {
    if (localCartItems.length) {
      return localCartItems.reduce((sum, item) => sum + getRentalLineTotal(item), 0);
    }
    return Number(totalCost || 0);
  }, [localCartItems, totalCost]);

  const handleRemoveItem = async (itemId) => {
    const result = await swal({
      title: 'Remove Booking?',
      text: 'Are you sure you want to remove this vehicle from your bookings?',
      icon: 'warning',
      buttons: ['Cancel', 'Remove'],
      dangerMode: true,
    });

    if (!result) return;

    setLoading(true);
    try {
      await cartAPI.delete(itemId);
      showAppToast({
        message: 'Booking removed from cart',
        type: 'success',
        duration: 2400,
      });
      onUpdate();
    } catch (error) {
      showAppToast({
        message: error.response?.data?.message || 'Failed to remove booking',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = () => {
    if (!localCartItems.length) {
      showAppToast({
        message: 'Your cart is empty',
        type: 'warning',
      });
      return;
    }
    trackBeginCheckout(localCartItems, subtotal);
    navigate('/checkout');
  };

  if (!localCartItems.length) {
    return (
      <div className="booking-page px-4 py-16">
        <div className="mx-auto max-w-md text-center">
          <div className="empty-booking-icon">
            <i className="bi bi-calendar2-x" />
          </div>
          <h1 className="section-title text-3xl text-primary-theme">Your Car Rental Bookings</h1>
          <p className="mt-3 text-secondary-theme">
            Cart is empty. Visit our vehicles page to book some vehicles.
          </p>
          <Link to="/vehicles" className="btn-luxury mt-8">
            Browse Vehicles
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-page px-4 py-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 text-center">
          <p className="text-accent">Reservations</p>
          <h1 className="section-title text-4xl text-primary-theme">Your Car Rental Bookings</h1>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="space-y-5 lg:col-span-2">
            {localCartItems.map((item) => {
              const days = getRentalDays(item);
              const lineTotal = getRentalLineTotal(item);
              const product = item.product || {};

              return (
                <article key={item.id} className="booking-item card-luxury">
                  <Link to={`/vehicle/show/${product.id}/${encodeURIComponent(product.name || 'vehicle')}`}>
                    <img
                      src={resolveImageUrl(product.imageURL)}
                      alt={product.name || 'Vehicle'}
                      className="booking-item-image"
                    />
                  </Link>

                  <div className="booking-item-body">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <Link
                          to={`/vehicle/show/${product.id}/${encodeURIComponent(product.name || 'vehicle')}`}
                          className="text-primary-theme no-underline"
                        >
                          <h2 className="card-title text-2xl">{product.name}</h2>
                        </Link>
                        <p className="mt-2 text-secondary-theme">
                          {product.description || 'Premium vehicle available for rent'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-secondary-theme">Price per day</p>
                        <p className="text-2xl font-bold text-accent">{formatMoney(product.price)}</p>
                      </div>
                    </div>

                    <div className="booking-meta-grid">
                      <span><strong>Pickup:</strong> {formatRentalDate(getBookedFrom(item))}</span>
                      <span><strong>Dropoff:</strong> {formatRentalDate(getBookedFor(item))}</span>
                      <span><strong>Dropoff time:</strong> {getDropoffTime(item) || 'Not selected'}</span>
                      <span><strong>Number of days:</strong> {days}</span>
                    </div>

                    <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-luxury pt-4">
                      <p className="text-lg text-primary-theme">
                        Total: <strong className="text-accent">{formatMoney(lineTotal)}</strong>
                      </p>
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(item.id)}
                        disabled={loading}
                        className="booking-remove"
                      >
                        Remove booking
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          <aside className="lg:col-span-1">
            <div className="booking-summary card-luxury">
              <h2 className="section-title text-2xl text-primary-theme">Booking Summary</h2>
              <div className="mt-6 space-y-4">
                <div className="flex justify-between text-secondary-theme">
                  <span>Vehicles</span>
                  <span>{localCartItems.length}</span>
                </div>
                <div className="flex justify-between text-secondary-theme">
                  <span>Rental total</span>
                  <span>{formatMoney(subtotal)}</span>
                </div>
                <div className="border-t border-luxury pt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xl font-bold text-primary-theme">Total</span>
                    <span className="text-3xl font-bold text-accent">{formatMoney(subtotal)}</span>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={handleCheckout}
                disabled={loading}
                className="btn-luxury mt-6 w-full"
              >
                Confirm Booking
              </button>

              <Link to="/vehicles" className="btn-luxury-outline mt-3 block w-full text-center">
                Continue Shopping
              </Link>

              <div className="booking-note">
                <strong>Flexible Booking</strong>
                <span>Free cancellation up to 24 hours before pickup.</span>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

export default Cart;
